import React, { InputHTMLAttributes, useEffect, useState, useRef, useCallback, forwardRef } from 'react'
import { Highlighter, Token, Typeahead, TypeaheadInputSingle, TypeaheadLabelKey, Hint } from 'react-bootstrap-typeahead'
import 'react-bootstrap-typeahead/css/Typeahead.css'
// import Form from 'react-bootstrap/Form'
import { Label } from '../Label'
import * as constant from '../../../../src/shared/util/constant'
import { ChangeEvent } from 'react'
import { Form, FloatingLabel } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { SelectOption } from '../../../shared/components/input'

// interface SelectOption<T> {
//   label: string
//   value: T
//   isInactive:boolean
//   count?:number
// }

interface Props<T> {
  id: string
  label?: string | React.ReactNode;
  controlId?: string;
  floatingLabelClass?: string;
  options: SelectOption[]
  sortOptions?: boolean
  defaultSelected?: SelectOption[]
  onChange?: (values: T[]) => void
  placeholder?: string
  multiple?: boolean
  disabled?: boolean
  isValid?: boolean
  isInvalid?: boolean
  feedback?: string
  inputProps?: InputHTMLAttributes<HTMLInputElement>,
  tabIndex?: number
  invalidWarning?: string
  invalidLableClass?: string
  labelKey?: TypeaheadLabelKey<string>
  isRequired?: boolean
  isEmpty?: boolean
  errorText?: string
  className?: string
  errorLableDivClassName?: string
  dropDownIconClassName?: string
  selectMainDivInvalidClassName?: string
  dropup?: boolean
  showAsFloatingLabel?: boolean
  showTitle?:boolean
  ref?: React.Ref<any>; // allow ref to be passed
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>; // allow onKeyDown to be passed
}

function SelectInner<T>(props: Props<T>, ref: React.Ref<HTMLInputElement>) {
  const {
    id,
    label,
    controlId,
    floatingLabelClass,
    options,
    sortOptions,
    defaultSelected,
    onChange,
    placeholder,
    multiple,
    disabled,
    isValid,
    isInvalid,
    feedback,
    inputProps,
    tabIndex,
    invalidWarning,
    invalidLableClass,
    labelKey,
    isRequired,
    className,
    errorLableDivClassName,
    dropDownIconClassName,
    selectMainDivInvalidClassName,
    dropup,
    showAsFloatingLabel = true,
    showTitle,
    onKeyDown,
    // isEmpty,
    // errorText
  } = props;
  const typeaheadRef = useRef<any>(null);

  let invalidLable = `cst-Feedback-Linebreak${invalidLableClass ? ' ' + invalidLableClass : ''}`;

  const [inputValue, setInputValue] = useState('');
  const [typeaheadKey, setTypeaheadKey] = useState(0);

  const [isExpanded, setIsExpanded] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const justSelectedRef = useRef(false); // Prevents reopening dropdown after Enter selection

  // Scroll the highlighted menu item into view
  const scrollHighlightedIntoView = useCallback((index: number) => {
    if (index < 0) return;
    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      const menuNode = typeaheadRef.current?.menuNode ||
        document.querySelector(`#${id} ~ .rbt-menu, [id="${id}"] .rbt-menu, .rbt-menu[id*="${id}"]`);
      if (!menuNode) return;
      const items = menuNode.querySelectorAll('.dropdown-item');
      const targetItem = items[index] as HTMLElement;
      if (targetItem) {
        targetItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    });
  }, [id]);

  // Scroll to highlighted item whenever it changes and apply highlight class to full row
  useEffect(() => {
    if (isExpanded && highlightedIndex >= 0) {
      scrollHighlightedIntoView(highlightedIndex);
    }
    // Apply/remove highlight class on the actual .dropdown-item elements
    requestAnimationFrame(() => {
      const menuNode = typeaheadRef.current?.menuNode ||
        document.querySelector(`#${id} ~ .rbt-menu, [id="${id}"] .rbt-menu, .rbt-menu[id*="${id}"]`);
      if (!menuNode) return;
      const items = menuNode.querySelectorAll('.dropdown-item');
      items.forEach((item: Element, idx: number) => {
        if (idx === highlightedIndex) {
          item.classList.add('keyboard-active');
        } else {
          item.classList.remove('keyboard-active');
        }
      });
    });
  }, [highlightedIndex, isExpanded, scrollHighlightedIntoView, inputValue]);

  // Get the filtered options based on the current input
  const getFilteredOptions = useCallback(() => {
    const sortedOptions = sortOptions === false
      ? options
      : [...options].sort((a, b) => (Number(b.isInactive === false) - Number(a.isInactive === false)) || a.label.localeCompare(b.label));

    if (!inputValue || (defaultSelected && defaultSelected.length > 0 && !multiple)) {
      return sortedOptions;
    }
    return sortedOptions.filter((option) =>
      option.label.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
    );
  }, [options, inputValue, sortOptions, defaultSelected, multiple]);

  // Keyboard handler for dropdown navigation
  const handleDropdownKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Tab key: close dropdown and let focus move naturally to next control
    if (e.key === 'Tab') {
      if (isExpanded) {
        setIsExpanded(false);
        typeaheadRef.current?.hideMenu();
        setHighlightedIndex(-1);
      }
      // Do NOT preventDefault — allow default tab behavior to move focus
      return;
    }

    // Get all visible items from DOM (including pagination option for keyboard navigation)
    const menuNode = typeaheadRef.current?.menuNode ||
      document.querySelector(`#${id} ~ .rbt-menu, [id="${id}"] .rbt-menu, .rbt-menu[id*="${id}"]`);
    const allVisibleItems = menuNode ? Array.from(menuNode.querySelectorAll('.dropdown-item')) : [];
    // Use filtered options length as fallback when DOM menu hasn't rendered yet
    const filteredOptions = getFilteredOptions();
    const maxIndex = allVisibleItems.length > 0
      ? allVisibleItems.length - 1
      : (filteredOptions.length > 0 ? filteredOptions.length - 1 : -1);

    if (e.key === 'ArrowDown') {
      e.preventDefault(); // Prevent page scrolling
      if (!isExpanded) {
        // Open dropdown on ArrowDown if closed
        setIsExpanded(true);
        typeaheadRef.current?.toggleMenu();
        setHighlightedIndex(0);
      } else if (maxIndex >= 0) {
        // Move highlight down, stop at last visible selectable item
        setHighlightedIndex((prev) => Math.min(prev + 1, maxIndex));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault(); // Prevent page scrolling
      if (isExpanded) {
        // Move highlight up, stop at first item (boundary condition)
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
      }
    } else if (e.key === 'Enter') {
      if (isExpanded && highlightedIndex >= 0 && highlightedIndex <= maxIndex) {
        e.preventDefault();
        const targetItem = allVisibleItems[highlightedIndex] as HTMLElement | undefined;
        // If the highlighted item is the pagination option, click it to load more results
        if (targetItem && targetItem.classList.contains('rbt-menu-pagination-option')) {
          targetItem.click();
          return;
        }
        // Select from visible items or fall back to filtered options
        let selectedOption: SelectOption | undefined;
        if (targetItem) {
          const itemText = targetItem.textContent?.replace(/^\*/, '') || '';
          selectedOption = filteredOptions.find(opt => opt.label === itemText) ||
            options.find(opt => opt.label === itemText);
        } else if (highlightedIndex < filteredOptions.length) {
          // Fallback when DOM not yet available (e.g., single option)
          selectedOption = filteredOptions[highlightedIndex];
        }
        if (selectedOption && !selectedOption.disabled) {
          if (onChange !== undefined) {
            onChange([selectedOption.value as T]);
          }
          justSelectedRef.current = true;
          setIsExpanded(false);
          typeaheadRef.current?.hideMenu();
          setHighlightedIndex(-1);
        }
      }
    } else if (e.key === 'Escape') {
      if (isExpanded) {
        e.preventDefault();
        e.stopPropagation();
        setIsExpanded(false);
        typeaheadRef.current?.hideMenu();
        setHighlightedIndex(-1);
      }
    }

    // Call the external onKeyDown handler if provided
    if (onKeyDown) {
      onKeyDown(e as any);
    }
  }, [isExpanded, highlightedIndex, getFilteredOptions, onChange, onKeyDown, id, options]);

  // Reset highlighted index when menu opens or filter changes
  useEffect(() => {
    if (isExpanded) {
      const filteredOptions = getFilteredOptions();
      let initialIndex = 0;
      if (defaultSelected && defaultSelected.length > 0) {
        const selectedIdx = filteredOptions.findIndex(
          (opt) => opt.value === defaultSelected[0]?.value
        );
        initialIndex = selectedIdx >= 0 ? selectedIdx : 0;
      }
      setHighlightedIndex(initialIndex);
      scrollHighlightedIntoView(initialIndex);
    } else {
      setHighlightedIndex(-1);
    }
  }, [isExpanded, inputValue]);

  // useEffect(() => {
  //   console.log('isExpanded changed:', isExpanded);
  // }, [isExpanded]);

  let renderTokenTemplate = multiple ? (option, { onRemove }, index) => {
    return (
      <>
  

        {multiple && <Token key={index} onRemove={!disabled? onRemove:""} option={option} className={option.isInactive ? "highlight-inactive" : option.isWarning ? "highlight-warning" : ""}>
          {`${option.isInactive ? "*" + option.label : option.label}`}
        </Token>}
      </>
    )
  } : null;

  let renderInputTemplate = multiple ? null : ({inputRef, referenceElementRef, ...inputProps }) => {
    //console.log('defaultSelected:',defaultSelected[0]?.label,inputProps)
    let a = inputProps.value !== "" && defaultSelected !== undefined && !multiple ? (defaultSelected[0]?.isInactive ? "*" + defaultSelected[0]?.label : defaultSelected[0]?.label) : defaultSelected.length === 0 ? '' : [];

    return (
      <>
        <Hint>
          <FloatingLabel controlId="floatingLabel" label={showAsFloatingLabel && <> {label} {isRequired && <span className="text-danger ms-1">*</span>} </> }>
            <Form.Control
              style={{paddingBottom: !showAsFloatingLabel?"0.625rem":"0rem"}}
              {...inputProps}
              className={`custom-dropdown${defaultSelected[0]?.isWarning ? ' highlight-warning' : ''}`}
              tabIndex={tabIndex}
              ref={node => {
                inputRef(node);
                referenceElementRef(node);
                if (typeof ref === 'function') ref(node);
                else if (ref) (ref as React.MutableRefObject<any>).current = node;
              }}
              onKeyDown={handleDropdownKeyDown}
            />
          </FloatingLabel>
        </Hint>
      </>
    )
  };

  let iconClassName = `dropdown-autocomplete-arrow${dropDownIconClassName ? ' ' + dropDownIconClassName : ''}`;
  let mainSelectDivClassName = `custom-dropdown ${selectMainDivInvalidClassName && isInvalid ? ' ' + selectMainDivInvalidClassName : ''} ${isInvalid ? 'invalid-dropdown' : ''}`;
  return (
    <>
      <div title={inputProps?.title ?? ''} className={mainSelectDivClassName} >
        {!disabled && <FontAwesomeIcon
          icon="angle-down"
          rotation={isExpanded ? 180 : undefined}
          style={{
            textAlign: "center",
            width: "10px",
            lineHeight: "10px",
            zIndex: 1,
            cursor: "pointer",
            transition: "transform 0.2s ease",
            pointerEvents: "none" // Make icon non-interactive
          }}
        />}
        <Typeahead<SelectOption>
          key={typeaheadKey}
          id={id}
          ref={typeaheadRef}
          dropup={dropup ? dropup : false}
          options={sortOptions === false ? options : [...options].sort((a, b) => (Number(b.isInactive === false) - Number(a.isInactive === false)) || a.label.localeCompare(b.label))}
          selected={defaultSelected}
          renderInput={renderInputTemplate}
          renderToken={renderTokenTemplate}
          className={className}
          caseSensitive={false}
          activeIndex={highlightedIndex}
          onChange={(selected: SelectOption[]) => {
            if (onChange !== undefined) {
              onChange(selected.map((option) => option.value as T))
            }
            setHighlightedIndex(-1);
          }}
          renderMenuItemChildren={(option, props, idx) => (
            <>
              <span title={showTitle && option.label} className={option.isInactive ? 'highlight-inactive' : option.isWarning ? 'highlight-warning' : ''}>
                <Highlighter search={props.text}>
                  {option.isInactive ? "*" + option.label : option.label}
                </Highlighter>
              </span>
              {option.count && <span className='count'>{option.count}</span>}
            </>
          )}

          placeholder={label}
          positionFixed={true}
          flip={true}
          multiple={multiple}
          disabled={disabled}
          isInvalid={isInvalid}
          inputProps={{
            ...inputProps,
            onKeyDown: multiple ? handleDropdownKeyDown : undefined,
          }}
          tabIndex={tabIndex}
          labelKey={labelKey}
          onInputChange={(text) => setInputValue(text)}
          onFocus={() => {
            // Prevent reopening dropdown immediately after keyboard selection
            if (justSelectedRef.current) {
              justSelectedRef.current = false;
              return;
            }
            setIsExpanded(true);
          }}
          onMenuToggle={(isOpen: boolean) => {
            //console.log('onMenuToggle fired:', isOpen);
            setIsExpanded(isOpen);
          }}
          onBlur={() => {
            // setIsExpanded(false);
            const match = options.some((option) =>
              option.label.toLowerCase() === inputValue.toLowerCase()
            );
            if (!match) {
              setInputValue('');
              setTypeaheadKey((prevKey) => prevKey + 1);
            }

            // if (ref.current?.state.showMenu && !isExpanded) {
            //   setIsExpanded(false);
            // } 
            setIsExpanded(false);
            // console.log(ref.current.state.showMenu);
            // console.log(isExpanded);
            // console.log('---');
          }}
          // onMenuToggle={(isOpen) => {
          //   console.log('onMenuToggle fired:', isOpen);
          //   setIsExpanded(isOpen);
          // }}
          filterBy={(option: SelectOption, selectProps: any) => {
            // change component default behavior
            // multiple - filter-out current selections
            const isMatch = option.label ? option.label.toLowerCase().indexOf(selectProps.text.toLowerCase()) !== -1 : false

            if (selectProps.selected.length && selectProps.multiple) {
              return selectProps.selected.every(
                (selected: any) => selected.label !== option.label && isMatch,
              )
            }
            // single (custom)- display all options
            if (selectProps.selected.length) {
              return true
            }
            // default filter as normal
            return isMatch
          }}
        />
        {/* {defaultSelected[0]?.isInactive && <span className='highlight-inactive'>{defaultSelected[0]?.label}</span>} */}
        {/* <div className={isValid ? 'is-valid' : isInvalid ? 'is-invalid' : undefined} /> */}
        <Form.Control.Feedback
          className={`text-left ml-3 mt-1 text-small cst-error ${isValid ? 'text-success' : isInvalid ? 'text-danger' : undefined
            }`}
          type={isValid ? 'valid' : 'invalid'}
        >
          {invalidWarning}
        </Form.Control.Feedback>
      </div>
      {/* <div className={(isInvalid ? `mt-n1 ${errorLableDivClassName ? errorLableDivClassName : ''}` : '')} >
        {(isInvalid) && <Label className={invalidLable}
          text={invalidWarning}
          style={{ color: "red" }} />}
      </div> */}

    </>
  )
}

const Select = forwardRef(SelectInner) as <T>(props: Props<T> & { ref?: React.Ref<HTMLInputElement> }) => JSX.Element;

Select.defaultProps = {
  defaultSelected: [],
  onChange: undefined,
  placeholder: '-- Choose --',
  multiple: false,
  disabled: false,
  isInvalid: false,
};

export { Select }
